/**
 * Helper (pure) อ่านสถานะจาก `progress` ของ run — ใช้ร่วมกันใน handler ที่ poll state ทั้ง 4 ตัว
 *
 * **สำคัญ:** ทั้ง 4 handler ตัดสิน `isGenerating` ด้วย "กติกาคนละชุด" โดยตั้งใจ ไฟล์นี้จึงรวมเฉพาะ
 * *ตัวอ่านค่าพื้นฐาน* ที่เขียนซ้ำกันเป๊ะ ๆ ไม่ได้รวมกติกาการตัดสินใจเข้าด้วยกัน
 *
 * ตารางนี้ไล่ตาม `isWorkflowDone` ของแต่ละ handler (`isGenerating` มักเป็นนิเสธของมัน)
 *
 * | handler | ตัดสินว่า workflow **จบ** เมื่อ |
 * |---|---|
 * | `HandleGetRunState` | `isCompleted` (ไม่มี next + มี values + ไม่มี step วิ่ง + ไม่อยู่ระหว่างเปลี่ยนเฟส) · หรือ next มี `upload_to_youtube` · หรือมี step error · หรือผู้ใช้กดหยุด |
 * | `HandleGetStory2SceneState` | merge state จาก run ก่อน แล้วจบเมื่อ เฟส story จบ (มี final_script + ไม่มี step วิ่ง + ยังไม่แตะ step ของ vdo) · หรือเฟส vdo จบ · หรือรอ upload · หรือมี step error |
 * | `HandleGetDirectScriptState` | แยกเคส pre-step ของ script2scene ออกไป return ก่อน · ที่เหลือใช้ `isCompleted` / รอ upload / มี step error — **`allStepsDone` ใช้เฉพาะสาขา `else` (fallback) ไม่ใช่กติกาหลัก** |
 * | `HandleGetScriptToSceneState` | ไม่มี next + มี `raw_scripts_images` + ไม่มี step วิ่ง · หรือมี step error |
 *
 * ข้อยกเว้นร่วม: ถ้า run เพิ่งถูกสั่งเริ่ม (`isJustStartedGenerate`) และยังไม่มี error
 * `HandleGetRunState` / `HandleGetDirectScriptState` จะยังถือว่า generating อยู่ แม้ `isWorkflowDone` เป็น true
 *
 * ค่า `progress` ที่เป็น null/undefined ให้ผลเหมือน "ยังไม่มีข้อมูล" (false) ส่วน `{}` (มี object แต่ว่าง)
 * ให้ผลตามความหมายของแต่ละฟังก์ชัน — ตรงกับพฤติกรรมเดิมของโค้ดที่ถูกแทน
 *
 * การตรวจ "ผู้ใช้กดหยุดเอง" (`isStoppedByUser` / `firstStoppedStep`) อยู่ไฟล์นี้ด้วย — ดู doc ของ `STOPPED_BY_USER`
 */

/** สถานะของ step เท่าที่ helper ชุดนี้ต้องรู้ (backend ส่ง field อื่นมาด้วย) */
export type StepProgressLike = { status?: string; error?: string; detail?: string };

type ProgressMap = Record<string, StepProgressLike> | null | undefined;

/**
 * step ที่ถือว่า "จบแล้ว" ในมุมของ direct script — ค่ามาจากโค้ดเดิมที่ถูกแทน (`HandleGetDirectScriptState`)
 *
 * ⚠ **คำศัพท์ในโมดูลนี้ยังไม่ตรงกัน — อย่าเชื่อค่านี้โดยไม่เช็ค backend**
 * - ที่นี่ + `anyStepError` ใช้ `done` / `cost_calculated` / `error`
 * - แต่ `MeGenInterface.ts` (handler เดียวกัน) เทียบ `=== "completed"` สำหรับ pre-step ของ script2scene
 *   และ `CardGenerateDirectScript.tsx` map `completed` → done, `failed` → error ตอน render
 * - `types/MeGenTypes.ts` ประกาศ `ProgressStepStatus = completed|running|failed|pending`
 * ⇒ ถ้า backend ส่ง `completed`/`failed` จริงสำหรับ direct script `allStepsDone` จะไม่มีวันเป็น true
 *   และ `anyStepError` จะไม่จับ `failed` (เป็นพฤติกรรมเดิม ไม่ได้เกิดจากการ refactor) — ต้องยืนยันกับ backend ก่อนแก้
 */
export const DONE_STEP_STATUSES = ["done", "cost_calculated"];

/** มี step ไหนกำลังวิ่งอยู่ไหม (`start` หรือ `running`) */
export const anyStepRunning = (progress: ProgressMap): boolean =>
  !!progress && Object.values(progress).some((p) => p?.status === "start" || p?.status === "running");

/** มี step ไหน error ไหม */
export const anyStepError = (progress: ProgressMap): boolean =>
  !!progress && Object.values(progress).some((p) => p?.status === "error");

/** ทุก step จบหมดแล้วไหม — ไม่มี progress = false, progress ว่าง (`{}`) = true */
export const allStepsDone = (progress: ProgressMap): boolean =>
  !!progress && Object.values(progress).every((p) => DONE_STEP_STATUSES.includes(p?.status || ""));

/** progress มี step เหล่านี้อย่างน้อย 1 ตัวไหม (ใช้ดูว่าเดินมาถึงเฟสไหนแล้ว) */
export const hasAnyStep = (progress: ProgressMap, steps: readonly string[]): boolean => {
  const keys = Object.keys(progress || {});
  return steps.some((step) => keys.includes(step));
};

/**
 * "ผู้ใช้กดหยุดเอง" — backend ส่งคนละข้อความใน 2 ระดับ ห้ามเทียบ `===` ข้ามระดับ (บั๊กเดิม)
 *
 * | ระดับ | field | ข้อความจริง |
 * |---|---|---|
 * | graph state | `values.error` (GET /run/{id}/state) · `episode.error` (GET /runs) | `"Generation stopped by user"` (graph.py:1383) · `"Lipsync generation stopped by user"` (client.py:1946) · `"FFmpeg task generation stopped by user"` (gpuhub_client.py:380) |
 * | progress step | `progress.<step>.error` | `"Stopped by user"` — เขียนโดย POST /run/{id}/stop เท่านั้น (server.py:1441) |
 * | progress step | `progress.<step>.detail` | `"Generation stopped by user"` — graph เขียนเอง (graph.py:1382) และ /stop ก็เขียน (server.py:1440) |
 *
 * ⇒ ใช้ `isStoppedByUser()` (contains ไม่สนตัวพิมพ์) กับทุกระดับ · `STOPPED_BY_USER` เหลือไว้เป็นข้อความบรรทัดใต้ step เท่านั้น
 */
const STOPPED_BY_USER_NEEDLE = "stopped by user";

/**
 * ข้อความที่ POST /run/{id}/stop เขียนลง `progress.<step>.error` (server.py:1441)
 * **ไม่ใช่** ค่าใน `values.error` / `episode.error` (ที่นั่นเป็น "Generation stopped by user" — ดูตารางด้านบน)
 * หน้าจอใช้ค่านี้เป็นข้อความสีเหลืองใต้ step ที่หยุดด้วย (CardGenerateVideo) — อย่าเอาไปเทียบ `===` กับ state error
 */
export const STOPPED_BY_USER = "Stopped by user";

/** ข้อความ error นี้แปลว่าผู้ใช้กดหยุดเอง (ไม่ใช่ error จริง) — รับได้ทั้ง values.error / episode.error / progress.<step>.error|detail */
export const isStoppedByUser = (message: string | null | undefined): boolean =>
  !!message && message.toLowerCase().includes(STOPPED_BY_USER_NEEDLE);

/**
 * step แรกที่ถูกผู้ใช้หยุด — ดูจาก `error` (POST /stop เขียน) หรือ `detail` (graph เขียน) ของ step · null = ไม่มี
 * ใช้แทน `next[0]`: หลัง stop กราฟ short-circuit ทุก node ไปค้างที่ interrupt ก่อน upload_to_youtube (graph.py:3111)
 * ทำให้ `next = ["upload_to_youtube"]` ไม่ใช่ step ที่หยุดจริง
 */
export const firstStoppedStep = (progress: ProgressMap): string | null =>
  Object.entries(progress || {}).find(([, p]) => isStoppedByUser(p?.error) || isStoppedByUser(p?.detail))?.[0] ?? null;
